import { enumRecordToCheckboxOptions } from '@umss/shared/utils';

export enum ContractorDocumentType {
  DRIVERS_LICENSE = 'DriversLicense',
  W9_FORM = 'W9Form',
  DIRECT_DEPOSIT_FORM = 'DirectDepositForm',
  CONTRACTOR_AGREEMENT = 'ContractorAgreement',
  BLS_CERTIFICATION = 'BlsCertification',
  CPR_CERTIFICATION = 'CprCertification',
  IMMUNIZATION_RECORDS = 'ImmunizationRecords',
  MEDICAL_LICENSE = 'MedicalLicense',
  BACKGROUND_CHECK = 'BackgroundCheck',
  PHOTO_ID = 'PhotoId',
  OTHER_CERTIFICATION = 'OtherCertification',
  I9_FORM = 'I9Form',
  RESUME = 'Resume'
}

export const CONTRACTOR_DOCUMENT_TYPE_RECORD: Record<
  ContractorDocumentType,
  string
> = {
  [ContractorDocumentType.DRIVERS_LICENSE]: 'Driver License',
  [ContractorDocumentType.W9_FORM]: 'W9 Form',
  [ContractorDocumentType.DIRECT_DEPOSIT_FORM]: 'Direct Deposit Form',
  [ContractorDocumentType.CONTRACTOR_AGREEMENT]: 'Contractor Agreement',
  [ContractorDocumentType.BLS_CERTIFICATION]: 'BLS Certification',
  [ContractorDocumentType.CPR_CERTIFICATION]: 'CPR Certification',
  [ContractorDocumentType.IMMUNIZATION_RECORDS]: 'Immunization Records',
  [ContractorDocumentType.MEDICAL_LICENSE]: 'Medical License',
  [ContractorDocumentType.BACKGROUND_CHECK]: 'Background Check',
  [ContractorDocumentType.PHOTO_ID]: 'Photo ID',
  [ContractorDocumentType.OTHER_CERTIFICATION]: 'Other Certification',
  [ContractorDocumentType.I9_FORM]: 'I-9 Form',
  [ContractorDocumentType.RESUME]: 'Resume'
};

export type DocumentListCategory =
  | DocumentListCategoryEnum
  | `${DocumentListCategoryEnum}`;

export enum DocumentListCategoryEnum {
  Certifications = 'certifications',
  Documents = 'documents',
  Forms = 'forms',
  Resume = 'resume'
}

export const DOCUMENT_LIST_CATEGORY_RECORD: Record<
  DocumentListCategoryEnum,
  string
> = {
  [DocumentListCategoryEnum.Certifications]: 'Certifications',
  [DocumentListCategoryEnum.Documents]: 'Documents',
  [DocumentListCategoryEnum.Forms]: 'Forms',
  [DocumentListCategoryEnum.Resume]: 'Resume'
};

export const DOCUMENT_LIST_CATEGORY_ICONS: Record<
  DocumentListCategoryEnum,
  string
> = {
  [DocumentListCategoryEnum.Certifications]: 'phosphorCertificateBold',
  [DocumentListCategoryEnum.Documents]: 'phosphorFileBold',
  [DocumentListCategoryEnum.Forms]: 'phosphorClipboardTextBold',
  [DocumentListCategoryEnum.Resume]: 'phosphorReadCvLogoBold'
};

export const DOCUMENT_LIST_CATEGORY_TYPE_RECORD: Record<
  DocumentListCategoryEnum,
  ContractorDocumentType[]
> = {
  [DocumentListCategoryEnum.Certifications]: [
    ContractorDocumentType.BLS_CERTIFICATION,
    ContractorDocumentType.CPR_CERTIFICATION,
    ContractorDocumentType.OTHER_CERTIFICATION,
    ContractorDocumentType.MEDICAL_LICENSE
  ],
  [DocumentListCategoryEnum.Documents]: [
    ContractorDocumentType.BACKGROUND_CHECK,
    ContractorDocumentType.CONTRACTOR_AGREEMENT,
    ContractorDocumentType.IMMUNIZATION_RECORDS,
    ContractorDocumentType.DRIVERS_LICENSE,
    ContractorDocumentType.PHOTO_ID
  ],
  [DocumentListCategoryEnum.Forms]: [
    ContractorDocumentType.DIRECT_DEPOSIT_FORM,
    ContractorDocumentType.W9_FORM,
    ContractorDocumentType.I9_FORM
  ],
  [DocumentListCategoryEnum.Resume]: [ContractorDocumentType.RESUME]
};

export const CERTIFICATION_TYPE_OPTIONS =
  enumRecordToCheckboxOptions<ContractorDocumentType>(
    CONTRACTOR_DOCUMENT_TYPE_RECORD,
    {
      includeValues:
        DOCUMENT_LIST_CATEGORY_TYPE_RECORD[
          DocumentListCategoryEnum.Certifications
        ],
      sortByLabel: 'includeValuesOrder'
    }
  );

export const DOCUMENT_TYPE_OPTIONS =
  enumRecordToCheckboxOptions<ContractorDocumentType>(
    CONTRACTOR_DOCUMENT_TYPE_RECORD,
    {
      includeValues:
        DOCUMENT_LIST_CATEGORY_TYPE_RECORD[DocumentListCategoryEnum.Documents],
      sortByLabel: 'includeValuesOrder'
    }
  );

export const DOCUMENT_FORMS =
  enumRecordToCheckboxOptions<ContractorDocumentType>(
    CONTRACTOR_DOCUMENT_TYPE_RECORD,
    {
      includeValues:
        DOCUMENT_LIST_CATEGORY_TYPE_RECORD[DocumentListCategoryEnum.Forms],
      sortByLabel: 'includeValuesOrder'
    }
  );

export const RESUME_DOCUMENT =
  enumRecordToCheckboxOptions<ContractorDocumentType>(
    CONTRACTOR_DOCUMENT_TYPE_RECORD,
    {
      includeValues:
        DOCUMENT_LIST_CATEGORY_TYPE_RECORD[DocumentListCategoryEnum.Resume],
      sortByLabel: 'includeValuesOrder'
    }
  );

export const DOCUMENT_LIST_CATEGORY_TYPE_OPTIONS: Record<
  DocumentListCategoryEnum,
  { value: ContractorDocumentType; label: string }[]
> = {
  [DocumentListCategoryEnum.Certifications]: CERTIFICATION_TYPE_OPTIONS,
  [DocumentListCategoryEnum.Documents]: DOCUMENT_TYPE_OPTIONS,
  [DocumentListCategoryEnum.Forms]: DOCUMENT_FORMS,
  [DocumentListCategoryEnum.Resume]: RESUME_DOCUMENT
};

/**
 * Generates grouped document type options by category.
 * Dynamically maps each category to its options with proper group labels.
 * This ensures scalability - new document types only need to be added to DOCUMENT_LIST_CATEGORY_TYPE.
 */
export const getGroupedDocumentTypeOptions = () => {
  return Object.values(DocumentListCategoryEnum).flatMap((category) => {
    const options = DOCUMENT_LIST_CATEGORY_TYPE_OPTIONS[category];
    const categoryLabel = DOCUMENT_LIST_CATEGORY_RECORD[category];
    return options.map((o) => ({ ...o, group: categoryLabel }));
  });
};

/**
 * Defines the category configuration required by the document list.
 * Accepts either the enum value or the string literal representation.
 */

export const CONTRACTOR_DOCUMENT_TYPE_OPTIONS =
  enumRecordToCheckboxOptions<ContractorDocumentType>(
    CONTRACTOR_DOCUMENT_TYPE_RECORD
  );
